/** A plain labeled bar chart answering "which sub-period was high or low".
 * Vertical bars grow up (green, profit) or down (red, loss) from a centre
 * baseline; the best and worst bars are ringed and called out above. Pure
 * HTML/CSS so every bar keeps a readable label. */

import { type ReactElement } from "react";

export interface LabeledBar {
  label: string;
  pnl: number;
  /** Optional second line under the label (e.g. the weekday). */
  sub?: string;
  /** Optional VIX value shown under the label (day bars). */
  vix?: number | null;
}

export function PeriodBars({ bars }: Readonly<{ bars: readonly LabeledBar[] }>): ReactElement {
  if (bars.length === 0)
    return <p className="text-sm text-ink-secondary">No trades in this period.</p>;
  const maxAbs = Math.max(1, ...bars.map((b) => Math.abs(b.pnl)));
  const best = extreme(bars, "max");
  const worst = extreme(bars, "min");
  return (
    <div>
      <Callout best={best} worst={worst} />
      <div className="mt-3 flex items-stretch gap-1 overflow-x-auto pb-1" style={{ height: 190 }}>
        {bars.map((b, i) => (
          <BarColumn
            key={b.label}
            bar={b}
            maxAbs={maxAbs}
            highlight={highlightFor(i, best, worst)}
          />
        ))}
      </div>
    </div>
  );
}

function BarColumn({
  bar,
  maxAbs,
  highlight,
}: Readonly<{
  bar: LabeledBar;
  maxAbs: number;
  highlight: "best" | "worst" | undefined;
}>): ReactElement {
  const pct = `${((Math.abs(bar.pnl) / maxAbs) * 100).toFixed(1)}%`;
  const ring = ringClass(highlight);
  return (
    <div className="flex min-w-[26px] flex-1 flex-col items-center" title={rupees(bar.pnl)}>
      <div className="flex w-full flex-1 items-end justify-center">
        {bar.pnl > 0 && (
          <div className={`w-3/4 rounded-t bg-success ${ring}`} style={{ height: pct }} />
        )}
      </div>
      <div className="h-px w-full bg-line" />
      <div className="flex w-full flex-1 items-start justify-center">
        {bar.pnl < 0 && (
          <div className={`w-3/4 rounded-b bg-danger ${ring}`} style={{ height: pct }} />
        )}
      </div>
      <div className="mt-1 max-w-full truncate text-[10px] font-medium text-ink-secondary">
        {bar.label}
      </div>
      {bar.sub !== undefined && <div className="text-[9px] text-ink-muted">{bar.sub}</div>}
      {bar.vix !== undefined && bar.vix !== null && (
        <div className="text-[9px] text-ink-muted">V{bar.vix.toFixed(1)}</div>
      )}
    </div>
  );
}

type Highlight = "best" | "worst" | undefined;

function highlightFor(i: number, best: Extreme, worst: Extreme): Highlight {
  if (i === best.i) return "best";
  if (i === worst.i) return "worst";
  return undefined;
}

function ringClass(highlight: Highlight): string {
  if (highlight === "best") return "ring-1 ring-success";
  if (highlight === "worst") return "ring-1 ring-danger";
  return "";
}

interface Extreme {
  i: number;
  bar: LabeledBar;
}

function extreme(bars: readonly LabeledBar[], kind: "max" | "min"): Extreme {
  // Caller guarantees a non-empty list; the fallback keeps types honest.
  let best: Extreme = { i: 0, bar: bars[0] ?? { label: "—", pnl: 0 } };
  bars.forEach((bar, i) => {
    if (kind === "max" ? bar.pnl > best.bar.pnl : bar.pnl < best.bar.pnl) best = { i, bar };
  });
  return best;
}

function Callout({ best, worst }: Readonly<{ best: Extreme; worst: Extreme }>): ReactElement {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <span className="rounded-ctl bg-success-bg px-2 py-1 text-success">
        Best · <span className="font-medium">{best.bar.label}</span> {rupees(best.bar.pnl)}
      </span>
      <span className="rounded-ctl bg-warning-bg px-2 py-1 text-warning">
        Worst · <span className="font-medium">{worst.bar.label}</span> {rupees(worst.bar.pnl)}
      </span>
    </div>
  );
}

function rupees(v: number): string {
  const sign = v < 0 ? "-" : "+";
  return `${sign}Rs ${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
}
